import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Workspace } from '@workspace/domain/workspace';
import { WorkspaceItem } from '../../items';
import { SidebarSearchViewModelItemContainer } from './SidebarSearchViewModelItemContainer';
import { SidebarSearchFolderItemContainer } from './SidebarSearchFolderItemContainer';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { ViewModelEntity } from '@view-model/domain/view-model';
import type { RouteParams } from '@user/pages/RouteParams';
import { useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { filteredAllSearchRecordsAtom } from './searchResultState';
import { sidebarSearchKeywordAtom } from '../sidebarSearchKeywordAtom';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';

type Props = {
  workspace: Workspace;
  rootFolderTree: RootFolderTree;
};

export const SidebarSearchWorkspaceItemContainer: React.FC<Props> = ({ workspace, rootFolderTree }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { folders, viewModels } = useAtomValue(filteredAllSearchRecordsAtom);
  const searchKeyword = useAtomValue(sidebarSearchKeywordAtom);

  useEffect(() => {
    if (searchKeyword) setIsOpen(true);
  }, [searchKeyword]);

  const toggleWorkspace = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const { workspaceId: workspaceIdParam, folderId: folderIdParam } = useParams<RouteParams>();
  const isSelected = useMemo(
    () => !folderIdParam && workspace.id === workspaceIdParam,
    [workspace.id, workspaceIdParam, folderIdParam]
  );

  const sortedFolders = useMemo(
    () =>
      rootFolderTree
        .getFolderIdsOfRoot()
        .map((folderId) => folders[folderId])
        .filter((folder) => !!folder)
        .sort(Folder.compare),
    [rootFolderTree, folders]
  );

  const sortedViewModels = useMemo(
    () =>
      rootFolderTree
        .getViewModelIdsOfRoot()
        .map((viewModelId) => viewModels[viewModelId])
        .filter((viewModel) => !!viewModel)
        .sort(ViewModelEntity.compare),
    [rootFolderTree, viewModels]
  );

  return (
    <WorkspaceItem
      workspace={workspace}
      isOpen={isOpen}
      toggleWorkspace={toggleWorkspace}
      isSelected={isSelected}
      searchKeyword={searchKeyword}
    >
      <>
        {sortedFolders.map((folder) => (
          <SidebarSearchFolderItemContainer
            key={folder.id}
            workspace={workspace}
            folder={folder}
            rootFolderTree={rootFolderTree}
          />
        ))}
        {sortedViewModels.map((viewModel) => (
          <SidebarSearchViewModelItemContainer key={viewModel.id} workspace={workspace} viewModel={viewModel} />
        ))}
      </>
    </WorkspaceItem>
  );
};
